import { 
  ChatCardAgent as IChatCardAgent,
  CompositeCard,
  CardType,
  CardData,
  CardContext,
  CardFilters,
  CardLocation
} from '../types/compositeCards';
import { CompositeCardFactory } from '../components/CompositeCards/Factory/CompositeCardFactory';
import { cardRegistry } from '../components/CompositeCards/Registry/CardComponentRegistry';

export class ChatCardAgent implements IChatCardAgent {
  private cards: Map<string, CompositeCard> = new Map();
  private threadCards: Map<string, string[]> = new Map();

  /**
   * Create a new card
   */
  async createCard(
    type: CardType,
    data: CardData,
    context: CardContext,
    location: CardLocation = CardLocation.CONTENT
  ): Promise<CompositeCard> {
    const card = CompositeCardFactory.createCard(type, data, context, location);
    
    // Validate card
    const validation = CompositeCardFactory.validateCard(card);
    if (!validation.isValid) {
      throw new Error(`Invalid card: ${validation.errors.join(', ')}`);
    }

    // Store card
    this.cards.set(card.id, card);

    // Associate with thread
    if (!this.threadCards.has(context.threadId)) {
      this.threadCards.set(context.threadId, []);
    }
    this.threadCards.get(context.threadId)!.push(card.id);

    // Register components if not already registered
    this.ensureComponentsRegistered();

    return card;
  }

  /**
   * Update an existing card
   */
  async updateCard(cardId: string, updates: Partial<CompositeCard>): Promise<CompositeCard> {
    const existingCard = this.cards.get(cardId);
    if (!existingCard) {
      throw new Error(`Card not found: ${cardId}`);
    }

    const updatedCard = CompositeCardFactory.updateCard(existingCard, updates);
    this.cards.set(cardId, updatedCard);

    return updatedCard;
  }

  /**
   * Delete a card
   */
  async deleteCard(cardId: string): Promise<void> {
    const card = this.cards.get(cardId);
    if (!card) {
      throw new Error(`Card not found: ${cardId}`);
    }

    // Remove from thread association
    const threadCards = this.threadCards.get(card.context.threadId);
    if (threadCards) {
      const index = threadCards.indexOf(cardId);
      if (index > -1) {
        threadCards.splice(index, 1);
      }
    }

    // Remove card
    this.cards.delete(cardId);
  }

  /**
   * Get cards by thread with optional filters
   */
  async getCardsByThread(threadId: string, filters?: CardFilters): Promise<CompositeCard[]> {
    const cardIds = this.threadCards.get(threadId) || [];
    const cards = cardIds.map(id => this.cards.get(id)).filter(Boolean) as CompositeCard[];

    if (filters) {
      return CompositeCardFactory.filterCards(cards, filters);
    }

    return cards;
  }

  /**
   * Pin a card
   */
  async pinCard(cardId: string): Promise<void> {
    const card = this.cards.get(cardId);
    if (!card) {
      throw new Error(`Card not found: ${cardId}`);
    }

    card.isPinned = true;
    this.cards.set(cardId, card);
  }

  /**
   * Unpin a card
   */
  async unpinCard(cardId: string): Promise<void> {
    const card = this.cards.get(cardId);
    if (!card) {
      throw new Error(`Card not found: ${cardId}`);
    }

    card.isPinned = false;
    this.cards.set(cardId, card);
  }

  /**
   * Register a card component
   */
  registerCardComponent(
    type: string,
    component: React.ComponentType<any>,
    role?: any,
    model?: any
  ): void {
    cardRegistry.registerBaseComponent(type, component);
  }

  /**
   * Get a card component
   */
  getCardComponent(
    type: string,
    role?: any,
    model?: any
  ): React.ComponentType<any> | null {
    return cardRegistry.getComponent(type, type, role || 'architect', model || 'Prime');
  }

  /**
   * Add card to message (placeholder for chat integration)
   */
  async addCardToMessage(messageId: string, card: CompositeCard): Promise<void> {
    // This would integrate with the chat message system
    // For now, we'll just store the association
    console.log(`Adding card ${card.id} to message ${messageId}`);
  }

  /**
   * Sync with chat context (placeholder for chat integration)
   */
  async syncWithChatContext(): Promise<void> {
    // This would sync card state with chat context
    console.log('Syncing cards with chat context');
  }

  /**
   * Get all cards
   */
  getAllCards(): CompositeCard[] {
    return Array.from(this.cards.values());
  }

  /**
   * Get card by ID
   */
  getCard(cardId: string): CompositeCard | undefined {
    return this.cards.get(cardId);
  }

  /**
   * Search cards
   */
  searchCards(query: string): CompositeCard[] {
    const allCards = this.getAllCards();
    return allCards.filter(card => {
      const summary = CompositeCardFactory.getCardSummary(card);
      return summary.toLowerCase().includes(query.toLowerCase());
    });
  }

  /**
   * Sort cards
   */
  sortCards(
    cards: CompositeCard[],
    sortBy: 'created' | 'updated' | 'name' | 'type' | 'pinned' = 'updated',
    order: 'asc' | 'desc' = 'desc'
  ): CompositeCard[] {
    return CompositeCardFactory.sortCards(cards, sortBy, order);
  }

  /**
   * Clone a card
   */
  async cloneCard(cardId: string, newContext?: Partial<CardContext>): Promise<CompositeCard> {
    const card = this.cards.get(cardId);
    if (!card) {
      throw new Error(`Card not found: ${cardId}`);
    }

    const clonedCard = CompositeCardFactory.cloneCard(card, newContext);
    this.cards.set(clonedCard.id, clonedCard);

    // Associate with thread
    if (!this.threadCards.has(clonedCard.context.threadId)) {
      this.threadCards.set(clonedCard.context.threadId, []);
    }
    this.threadCards.get(clonedCard.context.threadId)!.push(clonedCard.id);

    return clonedCard;
  }

  /**
   * Create context card from content card
   */
  async createContextCard(
    contentCardId: string,
    contextType: 'detail' | 'relationships' | 'history' | 'analytics'
  ): Promise<CompositeCard> {
    const contentCard = this.cards.get(contentCardId);
    if (!contentCard) {
      throw new Error(`Content card not found: ${contentCardId}`);
    }

    const contextCard = CompositeCardFactory.createContextCard(contentCard, contextType as any);
    this.cards.set(contextCard.id, contextCard);

    // Associate with thread
    if (!this.threadCards.has(contextCard.context.threadId)) {
      this.threadCards.set(contextCard.context.threadId, []);
    }
    this.threadCards.get(contextCard.context.threadId)!.push(contextCard.id);

    return contextCard;
  }

  /**
   * Ensure all required components are registered
   */
  private ensureComponentsRegistered(): void {
    // This would ensure all base components and role-specific components are registered
    // Silently check - no logging to avoid console spam
  }

  /**
   * Clear all cards (for testing/cleanup)
   */
  clearAllCards(): void {
    this.cards.clear();
    this.threadCards.clear();
  }

  /**
   * Get statistics about cards
   */
  getCardStatistics(): {
    totalCards: number;
    cardsByType: Record<string, number>;
    cardsByRole: Record<string, number>;
    cardsByModel: Record<string, number>;
    pinnedCards: number;
  } {
    const allCards = this.getAllCards();
    
    const cardsByType: Record<string, number> = {};
    const cardsByRole: Record<string, number> = {};
    const cardsByModel: Record<string, number> = {};
    let pinnedCards = 0;

    allCards.forEach(card => {
      // Count by type
      cardsByType[card.type] = (cardsByType[card.type] || 0) + 1;
      
      // Count by role
      cardsByRole[card.context.uiRole] = (cardsByRole[card.context.uiRole] || 0) + 1;
      
      // Count by model
      cardsByModel[card.context.modelType] = (cardsByModel[card.context.modelType] || 0) + 1;
      
      // Count pinned
      if (card.isPinned) {
        pinnedCards++;
      }
    });

    return {
      totalCards: allCards.length,
      cardsByType,
      cardsByRole,
      cardsByModel,
      pinnedCards,
    };
  }
}

// Export singleton instance
export const chatCardAgent = new ChatCardAgent();
export default chatCardAgent;
